import { RiUserLine } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import React from "react";
import { useFormContext } from "react-hook-form";
import type { DealParam } from "~/models/DealForm";
import { resetTenantValues, setTenantValues } from "~/models/DealForm";
import type { Leasing } from "~/models/Leasing";
import type { PropertyDealRoom } from "~/models/PropertyDeal";
import type { PropertyRoomOption } from "~/models/PropertyRoomOption";
import { formatDate, toDate } from "~/utils/DateUtil";
import { isFile } from "~/utils/FileUtil";
import Styles from "./Block.module.scss";
import { TenantBlockBase } from "./TenantBlockBase";

type Props = {
	currentRoom: PropertyDealRoom | PropertyRoomOption | null; // TODO: NSKDEV-1866でDealEditorBodyV2に切り替え後PropertyDealRoomは消す
	leasing: Leasing | null;
};

export const TenantBlock: React.FC<Props> = ({
	currentRoom = null,
	leasing = null
}: Props) => {
	const { formState, watch, setValue, getValues } = useFormContext<DealParam>();
	const { errors } = formState;

	React.useEffect(() => {
		if (leasing) {
			setValue("tenantName", leasing.tenantName);
			setValue("tenantNameKana", leasing.tenantNameKana);
			setValue("tenantTel", leasing.tenantTel);
			setValue("tenantEmail", leasing.tenantEmail);
		}
	}, [leasing, setValue]);
	const onChangeFile = React.useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			name: "leaseContractFile1" | "leaseContractFile2" | "leaseContractFile3"
		): void => {
			const file = e.target.files?.[0];
			if (file) {
				setValue(name, file);
			}
		},
		[setValue]
	);

	const onClickFileLink = React.useCallback(
		(
			e: React.MouseEvent,
			name: "leaseContractFile1" | "leaseContractFile2" | "leaseContractFile3"
		): void => {
			const file = getValues(name);
			if (isFile(file)) {
				e.preventDefault();
				const url = URL.createObjectURL(file);
				window.open(url);
			}
		},
		[getValues]
	);
	const showTenantFillButton =
		currentRoom !== null &&
		currentRoom?.tenantName !== "" &&
		currentRoom?.tenantName !== null;
	const leasingOriginStartDate = watch("leasingOriginStartDate");
	const leasingContractFile1 = watch("leaseContractFile1");
	const leasingContractFile2 = watch("leaseContractFile2");
	const leasingContractFile3 = watch("leaseContractFile3");
	return (
		<div className={Styles.Block}>
			<div className={Styles.BlockCaptionWrapper}>
				<div className={Styles.BlockCaption}>
					<span className={Styles.BlockIconWrapper}>
						<RiUserLine className={Styles.BlockIcon} />
					</span>
					<span className={Styles.BlockCaptionLabel}>入居者情報</span>
				</div>
			</div>
			<div className={Styles.BlockForms}>
				<div className={Styles.FormRow}>
					<div className={Styles.FormContent}>
						<Flex gap={16}>
							{showTenantFillButton ? (
								<Button
									height="small"
									onClick={(): void => {
										setTenantValues(setValue, currentRoom);
									}}
									variant="secondary"
								>
									{`${currentRoom.tenantName}の情報で補完する`}
								</Button>
							) : null}
							<Button
								height="small"
								onClick={(): void => {
									resetTenantValues(setValue);
								}}
								variant="secondary"
							>
								入居者情報をクリアする
							</Button>
						</Flex>
						{showTenantFillButton ? (
							<p className={Styles.Description}>
								補完には直近の案件情報を参照いたします。
							</p>
						) : null}
					</div>
				</div>
				<TenantBlockBase />
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>原契約開始日</span>
					</div>
					<div className={Styles.FormContent}>
						<InputDatev2
							maxWidth="13rem"
							onChange={(date) => {
								setValue("leasingOriginStartDate", formatDate(date));
							}}
							value={toDate(leasingOriginStartDate)}
						/>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>賃貸借契約書</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputFileGroup}>
							<div>
								{isFile(leasingContractFile1) && (
									<div>
										<a
											className={Styles.FileLink}
											href="#"
											onClick={(e: React.MouseEvent): void => {
												onClickFileLink(e, "leaseContractFile1");
											}}
											rel="noreferrer"
											target="_blank"
										>
											{leasingContractFile1.name}
										</a>
									</div>
								)}
								<div className={Styles.InputWrapper}>
									<input
										accept="image/*,application/pdf"
										onChange={(
											e: React.ChangeEvent<HTMLInputElement>
										): void => {
											onChangeFile(e, "leaseContractFile1");
										}}
										type="file"
									/>
								</div>
								{errors.leaseContractFile1 ? (
									<div className={Styles.ErrorMessage}>
										{errors.leaseContractFile1.message}
									</div>
								) : null}
							</div>

							<div>
								{isFile(leasingContractFile2) && (
									<div>
										<a
											className={Styles.FileLink}
											href="#"
											onClick={(e: React.MouseEvent): void => {
												onClickFileLink(e, "leaseContractFile2");
											}}
											rel="noreferrer"
											target="_blank"
										>
											{leasingContractFile2.name}
										</a>
									</div>
								)}
								<div className={Styles.InputWrapper}>
									<input
										accept="image/*,application/pdf"
										onChange={(
											e: React.ChangeEvent<HTMLInputElement>
										): void => {
											onChangeFile(e, "leaseContractFile2");
										}}
										type="file"
									/>
								</div>
								{errors.leaseContractFile2 ? (
									<div className={Styles.ErrorMessage}>
										{errors.leaseContractFile2.message}
									</div>
								) : null}
							</div>

							<div>
								{isFile(leasingContractFile3) && (
									<div>
										<a
											className={Styles.FileLink}
											href="#"
											onClick={(e: React.MouseEvent): void => {
												onClickFileLink(e, "leaseContractFile3");
											}}
											rel="noreferrer"
											target="_blank"
										>
											{leasingContractFile3.name}
										</a>
									</div>
								)}
								<div className={Styles.InputWrapper}>
									<input
										accept="image/*,application/pdf"
										onChange={(
											e: React.ChangeEvent<HTMLInputElement>
										): void => {
											onChangeFile(e, "leaseContractFile3");
										}}
										type="file"
									/>
								</div>
								{errors.leaseContractFile3 ? (
									<div className={Styles.ErrorMessage}>
										{errors.leaseContractFile3.message}
									</div>
								) : null}
							</div>
						</div>
						<p className={Styles.Description}>
							※工事に関する特約がある場合は、賃貸借契約書の添付をお願いします。
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
